// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Backend/connectivity status for the header "Live" indicator. Currently a
// lightweight browser-connectivity proxy (navigator.onLine + online/offline
// events); structured so it can later bind to a real BFF health poll without
// changing the header markup.

import { useEffect, useState } from 'react';

export type BackendState = 'live' | 'offline';

export interface BackendStatus {
  state: BackendState;
  label: string;
  /** Dot + text color token. */
  color: string;
}

const STATUS: Record<BackendState, BackendStatus> = {
  live: { state: 'live', label: 'Live', color: 'var(--st-ok)' },
  offline: { state: 'offline', label: 'Offline', color: 'var(--st-fault)' },
};

export function useBackendStatus(): BackendStatus {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  return online ? STATUS.live : STATUS.offline;
}
